import {
  ComponentType,
  createContext,
  Dispatch,
  MutableRefObject,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";

export type ConfirmComponentProps<T, Value = void> = {
  resolve: (params?: Value) => void;
  close: () => void;
  isOpen: boolean;
} & T;

type IContextConfirmComponent = {
  Component: MutableRefObject<ComponentType<
    ConfirmComponentProps<any, any>
  > | null>;
  resolve: MutableRefObject<(value?: any) => void>;
  setOpen: (isOpen: boolean) => void;
  setChildProps: (props: any) => void;
};

const ContextConfirmComponent = createContext<IContextConfirmComponent>(
  null as unknown as IContextConfirmComponent,
);

export const ProviderConfirmComponent = ({
  children,
}: PropsWithChildren<{}>) => {
  const [isOpen, setOpen] = useState(false);
  const [childProps, setChildProps] = useState({});
  const Component = useRef<ComponentType<
    ConfirmComponentProps<any, any>
  > | null>(null);
  const resolve = useRef<(value: any) => void>(() => {});

  const close = useCallback(() => {
    setOpen(false);
  }, []);

  const handleResolve = useCallback((...args) => {
    setTimeout(() => {
      resolve.current.apply(resolve.current, args as any);
    });
  }, []);

  const ChildComponent = Component.current;

  return (
    <ContextConfirmComponent.Provider
      value={{ Component, resolve, setChildProps, setOpen }}
    >
      {children}
      {ChildComponent && (
        <ChildComponent
          resolve={handleResolve}
          close={close}
          isOpen={isOpen}
          {...childProps}
        />
      )}
    </ContextConfirmComponent.Provider>
  );
};

export const useConfirmComponent = <OwnProps, Value>(
  Child: ComponentType<ConfirmComponentProps<OwnProps, Value>>,
  options?: {
    keepOpen?: boolean;
  },
) => {
  const {
    Component,
    setOpen,
    resolve: resolveRef,
    setChildProps,
  } = useContext(ContextConfirmComponent);

  const open = useCallback(
    (props: Omit<OwnProps, "resolve" | "close" | "isOpen">) =>
      new Promise((resolve: (value: Value) => void) => {
        Component.current = Child as any;

        setChildProps(props);

        resolveRef.current = ((value: Value) => {
          if (!options?.keepOpen) {
            setOpen(false);
          }

          resolve(value);
        }) as any;

        setOpen(true);
      }),
    [Child, Component, options?.keepOpen, resolveRef, setChildProps, setOpen],
  );

  const close = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  return {
    close,
    open,
    setProps: setChildProps as Dispatch<SetStateAction<OwnProps>>,
  };
};
